import { isEscapePressed, isTabPressed, TabIndex } from './keyboard';
import {
    isVisible,
    PrimaryInteractiveElements,
    SecondaryInteractiveElements,
} from './ui-helpers';
import { UIModal } from '../components/index';
import type { EventHandler } from './utils.type';

type ExitFocusTrapCallbackType = (event: KeyboardEvent) => void;

/**
 * @memberof SharedComponents
 * @class FocusHandler
 * @alias focusHandler
 */
export class FocusHandler {
    /**
     * State: Determines the element that has an active listener for focus trap
     * @memberof FocusHandler
     * @type {HTMLElement | undefined}
     * @private
     */
    trappedElement?: HTMLElement | undefined;
    /**
     * State: Determines the exit trap callback function for currently active element in focus trap
     * @memberof FocusHandler
     * @type {ExitFocusTrapCallbackType | undefined}
     * @private
     */
    trappedExitCallback?: ExitFocusTrapCallbackType | undefined;

    /**
     * List of all interactive elements.
     * Usually used to determine focusable elements.
     * @memberof FocusHandler
     * @type {Array<string>}
     */
    readonly InteractiveElements: Array<string> = [
        ...PrimaryInteractiveElements,
        ...SecondaryInteractiveElements,
    ];

    /**
     * DOM selector for all interactive elements
     * Usually used to query the full contents of focus-trapped element.
     * @memberof FocusHandler
     * @type {string}
     */
    readonly SelectorForAllFocusableElements: string =
        this.InteractiveElements.join(',');

    /**
     * Get the index position for given element. -1 if not found.
     * @memberof FocusHandler
     * @param {Element | null} currentFocusedElement
     * @param {Array<Element>} elementsToFocus
     * @returns {number}
     */
    getFocusIndexFor = (
        currentFocusedElement: Element | null,
        elementsToFocus: Array<HTMLElement>
    ): number => {
        return currentFocusedElement instanceof HTMLElement
            ? elementsToFocus.indexOf(currentFocusedElement)
            : -1;
    };

    /**
     * Returns the list of visible focusable elements for the given node.
     * Visibility check prevents focus elements array consisting e.g. dropdown options,
     * when dropdown is inside modal. This allows tabbing past the dropdown with a normal flow.
     * @memberof FocusHandler
     * @param {HTMLElement} parentElement
     * @returns {Array<HTMLElement>}
     */
    getFocusableElements = (parentElement: HTMLElement): Array<HTMLElement> => {
        const childElements: NodeListOf<HTMLElement> =
            parentElement.querySelectorAll(
                this.SelectorForAllFocusableElements
            );
        return [].filter.call(childElements, isVisible);
    };

    /**
     * Returns the first focusable element for the given node.
     * @memberof FocusHandler
     * @param {HTMLElement} parentElement
     * @returns {HTMLElement | null}
     */
    getFirstFocusableElement = (
        parentElement: HTMLElement
    ): HTMLElement | null => {
        return parentElement.querySelector(
            this.SelectorForAllFocusableElements
        );
    };

    /**
     * Focuses scroll target.
     * @memberof FocusHandler
     * @param {number | HTMLElement} target
     * @returns {void}
     */
    focusScrollTarget = (target: number | HTMLElement): void => {
        /** @type {HTMLElement} */
        if (target instanceof HTMLElement) {
            const previousTabIndex: number = target.tabIndex;
            target.tabIndex = Number(TabIndex.Inactive);
            target.focus();
            target.tabIndex = previousTabIndex;
        } else {
            this.getFirstFocusableElement(document.body)?.focus();
        }
    };

    /**
     * Forces the focus to stay within the parent element by making it jump from the last child to
     * the first one again and vice versa.
     *
     * Important note: Due to a11y should be used with caution. To be used only within elements that
     * have close buttons, so it is possible to get out of this trap. Exit trap callback can be used as fallback.
     * @memberof FocusHandler
     * @param {KeyboardEvent} event
     * @param {UIModal | HTMLElement} elementToTrap - parent element that the focus should be trapped within (e.g. modal).
     * @param {ExitFocusTrapCallbackType | undefined} [exitTrapCallback] - a function to call when ESC key is pressed within the trapped parent
     * @returns {void}
     */
    trapFocus = (
        event: KeyboardEvent,
        elementToTrap: HTMLElement | UIModal,
        exitTrapCallback?: ExitFocusTrapCallbackType
    ): void => {
        const isEscapeEvent = isEscapePressed(event);
        const isTabEvent = isTabPressed(event);
        // No proper event. Avoid influencing other events.
        if (!isEscapeEvent && !isTabEvent) {
            return;
        }

        const element = elementToTrap || this.trappedElement;
        // No element to trap
        if (!element) {
            return;
        }

        const exitCallback = exitTrapCallback ?? this.trappedExitCallback;
        // Allow exiting the trap via pressing ESC if callback provided
        if (isEscapeEvent && exitCallback) {
            event.preventDefault();
            exitCallback(event);
            return;
        }

        // Handle navigation with tab key
        if (isTabEvent) {
            event.preventDefault();
            const elementsToFocus: Array<HTMLElement> =
                this.getFocusableElements(element);
            if (elementsToFocus.length) {
                this.handleTabInFocusTrap(elementsToFocus, event);
            } else {
                element.focus();
            }
        }
    };

    /**
     * Handles TAB presses within given element to cycle through the interactive elements.
     * Enables jumping between first and last element to keep the focus in a cycle within the parent
     * Otherwise, allows tabbing back and forth one by one between all list elements.
     * @memberof FocusHandler
     * @param {NodeListOf<HTMLElement>} elementsToFocus
     * @param {KeyboardEvent} event
     * @returns {void}
     */
    handleTabInFocusTrap = (
        elementsToFocus: Array<HTMLElement>,
        event: KeyboardEvent
    ): void => {
        // Handle invalid parameters
        if (!elementsToFocus.length || !isTabPressed(event)) {
            return;
        }
        const visibleFocusableElements = elementsToFocus.filter((item) =>
            item.checkVisibility()
        );
        const indexOfFirstElement = 0;
        const indexOfLastElement = visibleFocusableElements.length - 1;
        const firstElement = visibleFocusableElements[indexOfFirstElement];
        const lastElement = visibleFocusableElements[indexOfLastElement];

        const indexOfCurrentFocus = this.getFocusIndexFor(
            document.activeElement,
            visibleFocusableElements
        );

        // If nothing is focused yet, first tab or tab+shift should focus on the first element
        if (indexOfCurrentFocus === -1) {
            firstElement?.focus();
            return;
        }
        // TAB+SHIFT press
        if (event.shiftKey) {
            // Jump from first to last
            if (indexOfCurrentFocus === indexOfFirstElement) {
                lastElement?.focus();
                return;
            }
            // Jump to previous if it exists, otherwise stay on current
            const previousElement =
                visibleFocusableElements[indexOfCurrentFocus - 1];
            if (previousElement) {
                previousElement.focus();
                return;
            }
            // TAB press
        } else {
            // Jump from last to first again
            if (indexOfCurrentFocus === indexOfLastElement) {
                firstElement?.focus();
                return;
            }
            // Jump to next if it exists, otherwise stay on current
            const nextElement =
                visibleFocusableElements[indexOfCurrentFocus + 1];
            if (nextElement) {
                nextElement.focus();
                return;
            }
        }
    };

    /**
     * @memberof FocusHandler
     * @param {HTMLElement} element
     * @param {ExitFocusTrapCallbackType} exitTrapCallback
     * @returns {void}
     */
    createFocusTrap = (
        element: HTMLElement,
        exitTrapCallback: ExitFocusTrapCallbackType
    ): void => {
        if (element) {
            this.trappedElement = element;
            this.trappedExitCallback = exitTrapCallback;
            document.addEventListener(
                'keydown',
                this.trapFocus as EventHandler
            );
        }
    };

    /**
     * @memberof FocusHandler
     * @param {HTMLElement} element
     * @param {ExitFocusTrapCallbackType} exitTrapCallback
     * @returns {void}
     */
    destroyFocusTrap = (
        element: HTMLElement,
        exitTrapCallback: ExitFocusTrapCallbackType
    ): void => {
        this.trappedElement = undefined;
        this.trappedExitCallback = undefined;
        document.removeEventListener('keydown', this.trapFocus as EventHandler);
    };
}

export const focusHandler = new FocusHandler();

/**
 * @typedef {ExitFocusTrapCallbackType} Function
 * @param {event} KeyobardEvent
 */
